import {
	type QueryClient,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query"
import type {
	CreateTopicBody,
	TopicListResponse,
	TopicResponse,
	UpdateTopicBody,
} from "@zplanem/responses"
import { apiFetch } from "../../lib/api-client"
import { queryKeys } from "../../lib/query-keys"
import { ticketBoardCacheRoot } from "./ticket-api-paths"
import { topicApiPath, topicsApiPath } from "./topic-api-paths"

export function useTopics(propertyId: string | undefined) {
	return useQuery({
		queryKey: propertyId
			? queryKeys.topics(propertyId)
			: ["properties", "__nil__", "topics"],
		queryFn: () =>
			apiFetch<TopicListResponse>(topicsApiPath(propertyId as string)).then(
				(response) => response.topics,
			),
		enabled: !!propertyId,
	})
}

function invalidateTopicConsumers(
	queryClient: QueryClient,
	propertyId: string,
) {
	return Promise.all([
		queryClient.invalidateQueries({ queryKey: queryKeys.topics(propertyId) }),
		queryClient.invalidateQueries({
			queryKey: ticketBoardCacheRoot(propertyId),
		}),
	])
}

export function useCreateTopic(propertyId: string) {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (body: CreateTopicBody) =>
			apiFetch<TopicResponse>(topicsApiPath(propertyId), {
				method: "POST",
				body: JSON.stringify(body),
			}).then((response) => response.topic),
		onSuccess: async () => {
			await invalidateTopicConsumers(queryClient, propertyId)
		},
	})
}

export function useUpdateTopic(propertyId: string) {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (input: { topicId: string; body: UpdateTopicBody }) =>
			apiFetch<TopicResponse>(topicApiPath(propertyId, input.topicId), {
				method: "PATCH",
				body: JSON.stringify(input.body),
			}).then((response) => response.topic),
		onSuccess: async () => {
			await invalidateTopicConsumers(queryClient, propertyId)
		},
	})
}

export function useDeleteTopic(propertyId: string) {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (topicId: string) =>
			apiFetch<{ deleted: true }>(topicApiPath(propertyId, topicId), {
				method: "DELETE",
			}),
		onSuccess: async () => {
			await invalidateTopicConsumers(queryClient, propertyId)
		},
	})
}
